import React, { useEffect, useMemo, useState } from "react";
import {
  Award,
  BadgeCheck,
  CircleDollarSign,
  Loader2,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Save,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import usePackkySetting from "../../Sevices/usePackkySetting";

const initialForm = {
  bonusId: "",
  fromPoints: "",
  toPoints: "",
  bonus: "",
  status: "Y",
};

const PointsTab = () => {
  const {
    fetchPerformancePointsBonusApi,
    insertPerformancePointsBonusApi,
    updatePerformancePointsBonusApi,
  } = usePackkySetting();

  const [bonusList, setBonusList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);

  const activeBonusCount = useMemo(() => {
    return bonusList.filter((item) => item?.status === "Y").length;
  }, [bonusList]);

  const inactiveBonusCount = useMemo(() => {
    return bonusList.filter((item) => item?.status !== "Y").length;
  }, [bonusList]);

  const highestBonus = useMemo(() => {
    if (!bonusList.length) return 0;

    return Math.max(...bonusList.map((item) => Number(item?.bonus || 0)));
  }, [bonusList]);

  const loadBonusData = (silent = false) => {
    fetchPerformancePointsBonusApi(setBonusList, setLoading, silent);
  };

  useEffect(() => {
    loadBonusData();
  }, []);

  const resetForm = () => {
    setFormData(initialForm);
    setShowForm(false);
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleEdit = (item) => {
    setFormData({
      bonusId: item?.pk_bonus_id || "",
      fromPoints: item?.from_points || "",
      toPoints: item?.to_points || "",
      bonus: item?.bonus || "",
      status: item?.status || "Y",
    });

    setShowForm(true);
  };

  const handleSave = () => {
    const fromPoints = Number(formData.fromPoints);
    const toPoints = Number(formData.toPoints);
    const bonus = Number(formData.bonus);

    if (!formData.fromPoints || !formData.toPoints || !formData.bonus) {
      alert("Please fill all required fields");
      return;
    }

    if (fromPoints >= toPoints) {
      alert("To Points should be greater than From Points");
      return;
    }

    if (bonus <= 0) {
      alert("Bonus should be greater than 0");
      return;
    }

    // EDIT MODE: backend currently supports only status update
    if (formData.bonusId) {
      updatePerformancePointsBonusApi(
        {
          bonusId: String(formData.bonusId),
          status: formData.status || "Y",
        },
        setSaving,
        () => {
          resetForm();
          loadBonusData(true);
        },
      );

      return;
    }

    // CREATE MODE
    insertPerformancePointsBonusApi(
      {
        fromPoints: String(formData.fromPoints),
        toPoints: String(formData.toPoints),
        bonus: String(formData.bonus),
        status: formData.status || "Y",
      },
      setSaving,
      () => {
        resetForm();
        loadBonusData(true);
      },
    );
  };

  const handleStatusToggle = (item) => {
    const nextStatus = item?.status === "Y" ? "N" : "Y";

    updatePerformancePointsBonusApi(
      {
        bonusId: String(item?.pk_bonus_id || ""),
        status: nextStatus,
      },
      setSaving,
      () => loadBonusData(true),
    );
  };

  const getStatusBadge = (status) => {
    const isActive = status === "Y";

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
          isActive
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-red-50 text-red-700 border border-red-200"
        }`}
      >
        <span
          className={`h-2 w-2 rounded-full ${
            isActive ? "bg-emerald-500" : "bg-red-500"
          }`}
        />
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      {/* HEADER */}
      <div className="rounded-[28px] border border-orange-200 bg-[#FFF7ED] p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
              <Sparkles size={14} />
              Performance Points Bonus
            </div>

            <h2 className="mt-3 text-2xl font-bold text-slate-900">
              Points Bonus Settings
            </h2>

            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Create point ranges and assign bonus amounts for drivers based on
              their weekly performance score.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => loadBonusData()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              type="button"
              onClick={() => {
                setFormData(initialForm);
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600"
            >
              <Plus size={16} />
              Add Bonus Range
            </button>
          </div>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Ranges</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">
                {bonusList.length}
              </p>
            </div>

            <div className="rounded-2xl bg-orange-50 p-3 text-orange-600">
              <Award size={24} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                Active Ranges
              </p>
              <p className="mt-2 text-3xl font-bold text-emerald-700">
                {activeBonusCount}
              </p>
            </div>

            <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600">
              <ShieldCheck size={24} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-red-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                Inactive Ranges
              </p>
              <p className="mt-2 text-3xl font-bold text-red-700">
                {inactiveBonusCount}
              </p>
            </div>

            <div className="rounded-2xl bg-red-50 p-3 text-red-600">
              <Power size={24} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                Highest Bonus
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900">
                ₹ {highestBonus}
              </p>
            </div>

            <div className="rounded-2xl bg-orange-50 p-3 text-orange-600">
              <CircleDollarSign size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* FORM */}
      {showForm && (
        <div className="rounded-[28px] border border-orange-100 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {formData.bonusId ? "Update Bonus Range" : "Create Bonus Range"}
              </h3>

              <p className="text-sm text-slate-500">
                Example: 700 to 800 points gives ₹100 bonus.
              </p>
            </div>

            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                From Points
              </label>

              <input
                type="number"
                value={formData.fromPoints}
                onChange={(e) =>
                  handleInputChange("fromPoints", e.target.value)
                }
                placeholder="700"
                className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-orange-400"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                To Points
              </label>

              <input
                type="number"
                value={formData.toPoints}
                onChange={(e) => handleInputChange("toPoints", e.target.value)}
                placeholder="800"
                className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-orange-400"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Bonus Amount
              </label>

              <input
                type="number"
                value={formData.bonus}
                onChange={(e) => handleInputChange("bonus", e.target.value)}
                placeholder="100"
                className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-orange-400"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Status
              </label>

              <select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-orange-400"
              >
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {formData.bonusId ? "Update Status" : "Save Bonus"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="overflow-hidden rounded-[28px] border bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b bg-slate-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Bonus Range List
            </h3>

            <p className="text-sm text-slate-500">
              Manage active and inactive bonus slabs for performance points.
            </p>
          </div>

          <div className="inline-flex w-fit items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">
            <TrendingUp size={14} />
            {bonusList.length} configured ranges
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-16 animate-pulse rounded-2xl bg-slate-100"
              />
            ))}
          </div>
        ) : bonusList.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
            <Award size={42} className="text-orange-300" />

            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              No bonus ranges found
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Add your first performance points bonus range.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-orange-400 text-left text-white">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Bonus ID</th>
                  <th className="px-4 py-3">Point Range</th>
                  <th className="px-4 py-3">Bonus</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {bonusList.map((item, index) => {
                  const isActive = item?.status === "Y";

                  return (
                    <tr
                      key={item?.pk_bonus_id || index}
                      className="border-t transition hover:bg-orange-50/40"
                    >
                      <td className="px-4 py-3 font-medium text-slate-500">
                        {index + 1}
                      </td>

                      <td className="px-4 py-3 font-semibold text-slate-800">
                        #{item?.pk_bonus_id || "-"}
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
                          {item?.from_points || 0} - {item?.to_points || 0} pts
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                          ₹ {Number(item?.bonus || 0)}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        {getStatusBadge(item?.status)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => handleStatusToggle(item)}
                            disabled={saving}
                            className={`inline-flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-70 ${
                              isActive
                                ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                : "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            }`}
                          >
                            {isActive ? (
                              <>
                                <Power size={14} />
                                Disable
                              </>
                            ) : (
                              <>
                                <BadgeCheck size={14} />
                                Enable
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsTab;
